import type { CSSProperties } from 'react';
import Image, { type ImageProps } from 'next/image';
import type { Media } from '../../payload-types';
import { cn } from '../../utilities/classname';
import { scaleDimensions } from '../utils/scale-dimensions';
import { makeRelativeUrl } from '../../utilities/make-relative-url';
import { isNumber } from '../../utilities/is-something';
import { makeMediaUrl } from '../../utilities/make-media-url';
import { opaqueBlurDataURL } from '../../utilities/media-placeholder/opaque-blur-data-url';
import { subjectAttributes, type SubjectAttributes } from '../../utilities/change-requests/subject-marks';

/**
 * The source and box `MediaElement` renders `data` at, without rendering it.
 *
 * The box is the requested `size` variant's when it has a url, else the
 * original's; explicit `width`/`height` win over the stored dimensions, and the
 * pair is scaled down (never up) to fit `maxWidth`/`maxHeight`. With
 * `coverSide` it is instead measured from the original wherever the original's
 * dimensions are stored (a variant's height is rounded, so its aspect is not
 * exact) and scaled up or down to cover that square (see `coverDimensions`). A
 * dimension that is not stored comes back as `0`, exactly as MediaElement
 * passes it on, and no scaling is applied then.
 *
 * Without `size`, the source is the original. With it, the source is the
 * smallest stored file at least twice the box's width (`retinaSource`), so a
 * variant measures the box but is not necessarily the file loaded. Returns
 * `null` when neither the variant nor the original has a url (MediaElement
 * renders nothing).
 *
 * `url` is the stored url, BEFORE `makeMediaUrl` / `makeRelativeUrl`: a caller
 * that only needs the box (the header-height estimate) never needs the
 * rewritten url.
 */
export const resolveMediaDimensions = (
  data: Media,
  opts: {
    size?: keyof NonNullable<Media['sizes']>;
    width?: number;
    height?: number;
    maxHeight?: number;
    maxWidth?: number;
    coverSide?: number;
  },
): { url: string; width: number; height: number } | null => {
  const mediaSize = opts.size && data.sizes?.[opts.size];
  const media = mediaSize?.url
    ? { url: mediaSize.url, width: mediaSize.width, height: mediaSize.height }
    : data.url
      ? { url: data.url, width: data.width, height: data.height }
      : null;
  if (!media) { return null; }

  const measured = opts.coverSide && isNumber(data.width) && isNumber(data.height)
    ? { width: data.width, height: data.height }
    : media;
  const imgHeight = opts.height ?? measured.height ?? 0;
  const imgWidth = opts.width ?? measured.width ?? 0;

  const unscaled = { height: imgHeight, width: imgWidth };
  const dimensions = !imgHeight || !imgWidth
    ? unscaled
    : opts.coverSide
      ? coverDimensions(unscaled, opts.coverSide)
      : scaleDimensions(unscaled, { maxHeight: opts.maxHeight, maxWidth: opts.maxWidth });

  const url = opts.size && dimensions.width
    ? retinaSource(data, dimensions.width) ?? (data.url ? data.url : media.url)
    : media.url;

  return { url, width: dimensions.width, height: dimensions.height };
};

/**
 * `dimensions` scaled, up or down, until the shorter side is `side`: what the
 * browser draws when CSS cover-crops an image into a `side` square (a daisyUI
 * `.avatar`, whose img is 100% by 100% and `object-fit: cover`). The longer
 * side rounds up, so given exact dimensions the box is never narrower than
 * what is drawn.
 */
const coverDimensions = (
  { width, height }: { width: number; height: number },
  side: number,
): { width: number; height: number } => width >= height
  ? { width: Math.ceil((width * side) / height), height: side }
  : { width: side, height: Math.ceil((height * side) / width) };

/**
 * The smallest stored file, variant or original, at least twice `boxWidth`:
 * the file a `size` caller's image is cut from. Next cuts a fixed box's 1x and
 * 2x srcset candidates from the source and never enlarges, so a variant only
 * as wide as its box (a logo drawn 128 wide from its 128 variant) came back at
 * 1x for the 2x request and retina screens stretched it. A variant, not the
 * original, wherever one is wide enough: Next sends an animated file whole,
 * and Payload's variants are animated copies at their own width; and an
 * original can be missing from storage while its variants survive (see
 * `repair-media-keys-cli.ts`). `null` when no file with a stored width is
 * wide enough; `resolveMediaDimensions` then loads the original, or the
 * measuring variant when the original has no url.
 */
const retinaSource = (data: Media, boxWidth: number): string | null => {
  let best: { url: string; width: number } | null = null;
  for (const file of [...Object.values(data.sizes ?? {}), data]) {
    if (file?.url && isNumber(file.width) && file.width >= boxWidth * 2 && (!best || file.width < best.width)) {
      best = { url: file.url, width: file.width };
    }
  }
  return best?.url ?? null;
};

/**
 * The `object-fit` Next needs to size a blur background the way the image is
 * drawn, used when the caller sets none inline. MediaElement sets fit through
 * its class list (`object-cover` unless the caller's class says
 * `object-contain`; tailwind-merge keeps one), which Next cannot read — without
 * this a `fill` image's blur is stretched. Only the base class counts; a
 * breakpoint variant is ignored.
 *
 * The inline fit is not blur-only: it stays on the loaded image and beats any
 * class, so with the blur on the image is pinned to cover/contain. Do not opt in
 * an image that relies on a responsive `md:object-*` class or on
 * `object-fill|none|scale-down` (or pass that fit in `style.objectFit`).
 */
export const blurObjectFit = (className: string): 'contain' | 'cover' =>
  /(^|\s)object-contain(\s|$)/.test(className) ? 'contain' : 'cover';

export const MediaElement: React.FC<{
  data: Media;
  /**
   * The stored variant whose dimensions set the box, unless `width`/`height`
   * or `coverSide` are given. The file loaded is the smallest stored one at least twice the
   * box's width, which is often a larger variant (see `retinaSource`).
   *
   * That is only sharp when CSS draws the image at the box. An image drawn at
   * a fixed height with `w-auto` needs a `maxHeight` and a variant at least
   * that tall; one cover-cropped into a square needs `coverSide`.
   */
  size?: keyof NonNullable<Media['sizes']>;
  /**
   * The side of the square CSS cover-crops the image into, e.g. `48` for a
   * daisyUI `.avatar` at `size-12`. The box becomes the image, measured from
   * the original, scaled until its shorter side is `coverSide`, so a 3:2 photo
   * is boxed 72x48 (what the circle draws) rather than at its 48 variant's
   * 48x32, which left Next's 1x and 2x candidates two-thirds the width needed.
   * `maxWidth`/`maxHeight` are ignored with it. Pair it with `size`, or the
   * original is loaded.
   */
  coverSide?: number;
  fill?: boolean;
  className?: string;
  /**
   * Overrides the media's stored alt text. Pass `''` for a decorative copy of
   * an image shown beside it, so a screen reader announces the image once.
   */
  alt?: string;
  height?: number;
  width?: number;
  /**
   * Shortcut for "this is the LCP image": injects a `<link rel="preload">`
   * and sets `loading="eager"` + `fetchPriority="high"`. Use on at most ONE
   * image per page — typically the hero. Explicit `loading` / `fetchPriority`
   * props win when set.
   */
  priority?: boolean;
  /**
   * Force `<img loading>`. When unset, defaults to `priority ? 'eager' : 'lazy'`.
   * Pass `'eager'` for above-fold-but-not-LCP images (e.g. hero strip logos).
   */
  loading?: 'eager' | 'lazy';
  /**
   * Force the `fetchpriority` hint. When unset, defaults to `priority ? 'high' : 'auto'`.
   * Reserve `'high'` for the LCP image so the hint isn't diluted.
   */
  fetchPriority?: 'high' | 'low' | 'auto';
  maxHeight?: number;
  maxWidth?: number;
  sizes?: string;
  /**
   * Additional inline styles merged with the internal `object-position`. Use
   * to apply CSS clamps that must resolve directly on the rendered <img> —
   * notably `max-height` in px, which `max-h-full` cannot express because the
   * percentage fails to resolve against most hero wrappers.
   */
  style?: CSSProperties;
  /**
   * Show the image's stored blur (`data.placeholder.blurDataURL`) while it
   * loads. Off by default: turn it on only where a visitor is likely to watch
   * the image load — heroes, the first items of a carousel or grid, a
   * template's top image. Each one adds ~0.8KB to the HTML and again to the RSC
   * payload, but no extra image request.
   *
   * Used only for an OPAQUE image (`placeholder.opaque`), as in
   * `renderArtDirectedPicture`: Next removes the blur through React state, i.e.
   * only after hydration, and an opted-in image is usually a preloaded hero that
   * has loaded by then, so a transparent one (a cut-out, an `object-contain`
   * logo) would paint over a blurred copy of itself until hydration, and for
   * good if the JS never runs.
   *
   * With the blur on, the image also gets an inline `object-fit` (the caller's
   * `style.objectFit`, else `blurObjectFit` of the class list) that stays after
   * load — see `blurObjectFit` before opting in a responsively fitted image.
   */
  placeholder?: boolean;
  /**
   * The image's Subject attribute, from a server parent. A client component
   * that renders this passes it (e.g. `subjects?.[image.id]` from a
   * `subjectsById` map): computed here, the mark is always empty in a client
   * render. When given it is spread as is; left out, a server render computes
   * the image's own.
   */
  subject?: SubjectAttributes;
}> = ({
  data,
  size,
  fill,
  className,
  alt,
  height,
  width,
  priority,
  loading,
  fetchPriority,
  maxHeight,
  maxWidth,
  coverSide,
  sizes,
  style,
  placeholder,
  subject,
}) => {
    const dimensions = resolveMediaDimensions(data, { size, width, height, maxHeight, maxWidth, coverSide });
    if (!dimensions) { return null; }

    const url = makeRelativeUrl(makeMediaUrl(data, dimensions.url));
    const resolvedAlt = alt ?? data.alt ?? '';

    const objectPosition = isNumber(data.focalX) && isNumber(data.focalY) ? `${data.focalX}% ${data.focalY}%` : undefined;
    const defaultClassName = 'object-cover';
    const resolvedClassName = cn(defaultClassName, className);
    const resolvedLoading: 'eager' | 'lazy' | undefined =
      loading ?? (priority ? 'eager' : undefined);
    const resolvedFetchPriority: 'high' | 'low' | 'auto' =
      fetchPriority ?? (priority ? 'high' : 'auto');

    const blurDataURL = placeholder ? opaqueBlurDataURL(data) : undefined;
    const blurProps: Pick<ImageProps, 'placeholder' | 'blurDataURL'> = blurDataURL
      ? { placeholder: 'blur', blurDataURL }
      : {};
    const imageStyle: CSSProperties = blurDataURL
      ? { ...style, objectPosition, objectFit: style?.objectFit ?? blurObjectFit(resolvedClassName) }
      : { ...style, objectPosition };
    // Names the image's Media document on a Preview Site (nothing elsewhere);
    // Next passes `data-*` props through to the <img>.
    const marks = subject ?? subjectAttributes({ collection: 'media', id: data.id });

    if (fill) {
      return (
        <Image
          src={url}
          alt={resolvedAlt}
          fill={fill}
          className={resolvedClassName}
          priority={priority}
          loading={resolvedLoading}
          fetchPriority={resolvedFetchPriority}
          sizes={sizes}
          style={imageStyle}
          {...blurProps}
          {...marks}
        />
      );
    }

    return (
      <Image
        src={url}
        alt={resolvedAlt}
        height={dimensions.height}
        width={dimensions.width}
        className={resolvedClassName}
        priority={priority}
        loading={resolvedLoading}
        fetchPriority={resolvedFetchPriority}
        sizes={sizes}
        style={imageStyle}
        {...blurProps}
        {...marks}
      />
    );
  };
